"use client"

import { useState } from "react"
import { ArrowRight, Lightbulb } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible"
import { Skeleton } from "@/components/ui/skeleton"
import {
  OUTCOME_METRIC_DEFINITIONS,
  UNRESOLVED_TOPIC_MIN_SESSIONS,
} from "@/lib/conversation-quality/metric-definitions"
import { waitingSummary } from "@/lib/conversation-quality/change-display"
import {
  OUTCOME_CHART_COLORS,
  OUTCOME_DEFINITIONS,
  OUTCOME_LABELS,
  TOPIC_CATEGORY_LABELS,
} from "@/lib/conversation-quality/outcome-display"
import type { QualitySuggestionsResult, UnresolvedTopicsResult } from "@/server-actions/conversation-quality"

import {
  EmptyNote,
  LANDING_SCROLL_MARGIN,
  revealSuggestion,
  RowTrigger,
  SectionCard,
  SegmentKey,
  SessionLinks,
  sessionCount,
  StackedBar,
  useLandedScroll,
  type Segment,
} from "./section-parts"

type Category = UnresolvedTopicsResult["categories"][number]
type Topic = Category["topics"][number]
type Suggestion = QualitySuggestionsResult["suggestions"][number]
type Split = Pick<Topic, "handedToTeam" | "gaveUp">

const TOP_CATEGORIES = 5
const SUMMARY_CATEGORIES = 3

// A rule verdict — nobody answered — records no question, so an empty list is not «nothing unresolved».
const NO_QUESTIONS = "No question was recorded from the requests the agent didn't resolve in this period."

/** The two endings worth fixing; the rest of a group's sessions were resolved by the team. */
const SPLIT_KEY: Segment[] = [
  {
    key: "handedToTeam",
    label: OUTCOME_LABELS.escalated,
    count: 0,
    color: OUTCOME_CHART_COLORS.escalated,
    definition: OUTCOME_DEFINITIONS.escalated,
  },
  {
    key: "gaveUp",
    label: "Customer gave up",
    count: 0,
    color: OUTCOME_CHART_COLORS.abandoned,
    definition:
      "Nobody from your team stepped in: the customer left dissatisfied with the agent's replies, or stopped answering its question.",
  },
]

/** Kinds of question, the busiest first; a kind opens into its questions, a question into its examples. */
export function UnresolvedTopics({
  data,
  suggestions,
  landing,
  onLanded,
}: {
  data: UnresolvedTopicsResult | undefined;
  /** The waiting drafts, linked from the question each was drafted for. */
  suggestions: Suggestion[] | undefined;
  /** A link opened the tab to land here. */
  landing: boolean;
  onLanded: () => void;
}) {
  const [showAll, setShowAll] = useState(false)
  const categories = data?.categories
  const shown = showAll ? categories : categories?.slice(0, TOP_CATEGORIES)
  const ref = useLandedScroll<HTMLDivElement>(landing, categories !== undefined, onLanded)

  return (
    <SectionCard
      ref={ref}
      className={LANDING_SCROLL_MARGIN}
      title={OUTCOME_METRIC_DEFINITIONS.unresolvedTopics.label}
      definition={OUTCOME_METRIC_DEFINITIONS.unresolvedTopics.definition}
      subtitle="by kind of question"
    >
      {!categories || !shown ? (
        <Skeleton className="h-40 w-full" />
      ) : categories.length === 0 ? (
        <EmptyNote text={NO_QUESTIONS} />
      ) : (
        <div className="space-y-3">
          <SegmentKey segments={SPLIT_KEY} />
          <ul className="divide-y">
            {shown.map((category) => (
              <CategoryRow key={category.category} category={category} suggestions={suggestions} />
            ))}
          </ul>
          {categories.length > TOP_CATEGORIES && (
            <Button variant="link" size="sm" className="h-auto px-0" onClick={() => setShowAll(!showAll)}>
              {showAll ? `Show the top ${TOP_CATEGORIES}` : `Show all ${categories.length} kinds`}
            </Button>
          )}
        </div>
      )}
    </SectionCard>
  )
}

function CategoryRow({ category, suggestions }: { category: Category; suggestions: Suggestion[] | undefined }) {
  const keys = new Set(category.topics.map((topic) => topic.key))
  const waiting = suggestions?.filter((suggestion) => keys.has(suggestion.topicKey)).length ?? 0

  return (
    <li className="py-3">
      <Collapsible>
        <RowTrigger>
          <GroupHeading
            label={TOPIC_CATEGORY_LABELS[category.category]}
            sessions={category.sessions}
            split={category}
            waiting={waiting}
          />
        </RowTrigger>
        <SplitBar split={category} total={category.sessions} className="ml-6 mt-2" />
        <CollapsibleContent className="ml-6 mt-3">
          {category.topics.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No single question of this kind came up in {UNRESOLVED_TOPIC_MIN_SESSIONS} sessions or more.
            </p>
          ) : (
            <ul className="space-y-3 border-l pl-3">
              {category.topics.map((topic) => (
                <TopicRow
                  key={topic.key}
                  topic={topic}
                  suggestions={suggestions?.filter((suggestion) => suggestion.topicKey === topic.key) ?? []}
                />
              ))}
            </ul>
          )}
        </CollapsibleContent>
      </Collapsible>
    </li>
  )
}

function TopicRow({ topic, suggestions }: { topic: Topic; suggestions: Suggestion[] }) {
  const examples = topic.examples ?? []
  // Labels are model-written, and their casing varies from one review to the next.
  const heading = (
    <GroupHeading label={topic.label} sessions={topic.sessions} split={topic} labelClassName="first-letter:uppercase" />
  )

  if (examples.length === 0 && suggestions.length === 0) {
    return <li className="pl-6">{heading}</li>
  }

  return (
    <li>
      <Collapsible>
        <RowTrigger>{heading}</RowTrigger>
        <CollapsibleContent className="ml-6 mt-2 space-y-2">
          <SessionLinks label="Examples" sessions={examples} />
          {suggestions.map((suggestion) => (
            <div key={suggestion.id} className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="secondary" className="gap-1 font-medium">
                <Lightbulb className="h-3 w-3" aria-hidden="true" />
                Waiting for your decision
              </Badge>
              <Button variant="link" size="sm" className="h-auto px-0 text-xs" onClick={() => revealSuggestion(suggestion.id)}>
                Show it
              </Button>
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </li>
  )
}

/** A name and its sessions on one line, how they ended beneath — it wraps at any width. */
function GroupHeading({
  label,
  sessions,
  split,
  labelClassName = "font-medium",
  waiting = 0,
}: {
  label: string;
  sessions: number;
  split: Split;
  labelClassName?: string;
  /** Drafts waiting for a decision on the group's questions. */
  waiting?: number;
}) {
  return (
    <div className="min-w-0 flex-1 space-y-0.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className={labelClassName}>{label}</span>
          {waiting > 0 && (
            <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-800 dark:bg-violet-900/60 dark:text-violet-200">
              {waiting === 1 ? "1 change waits" : `${waiting.toLocaleString()} changes wait`}
            </span>
          )}
        </span>
        <span className="shrink-0 text-muted-foreground tabular-nums">{sessionCount(sessions)}</span>
      </div>
      <p className="text-xs text-muted-foreground">{describeSplit(split)}</p>
    </div>
  )
}

function SplitBar({ split, total, className }: { split: Split; total: number; className?: string }) {
  return <StackedBar segments={splitSegments(split)} total={total} className={className} />
}

/**
 * Overview's glance at Learning & improvements: the busiest kinds of question and how many drafts wait, with a
 * way to the tab that holds both.
 */
export function UnresolvedSummary({
  data,
  waiting,
  onOpen,
}: {
  data: UnresolvedTopicsResult | undefined;
  waiting: number | undefined;
  onOpen: () => void;
}) {
  const categories = data?.categories

  return (
    <SectionCard
      title="What customers asked that the agent didn't resolve"
      subtitle={
        categories &&
        categories.length > 0 &&
        `top ${Math.min(SUMMARY_CATEGORIES, categories.length)} of ${categories.length} kinds of question`
      }
    >
      {!categories ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <div className="space-y-4">
          {categories.length === 0 ? (
            <EmptyNote text={NO_QUESTIONS} />
          ) : (
            <>
              <ul className="space-y-3">
                {categories.slice(0, SUMMARY_CATEGORIES).map((category) => (
                  <li key={category.category} className="space-y-1.5">
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span>{TOPIC_CATEGORY_LABELS[category.category]}</span>
                      <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                        {sessionCount(category.sessions)}
                      </span>
                    </div>
                    <SplitBar split={category} total={category.sessions} className="h-2" />
                  </li>
                ))}
              </ul>
              <SegmentKey segments={SPLIT_KEY} />
            </>
          )}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t pt-3 text-sm">
            {waiting !== undefined && (
              <span className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span>{waitingSummary(waiting)}</span>
              </span>
            )}
            <Button variant="link" size="sm" className="h-auto px-0" onClick={onOpen}>
              Learning &amp; improvements
              <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  )
}

function splitSegments(split: Split): Segment[] {
  return [
    { ...SPLIT_KEY[0], count: split.handedToTeam },
    { ...SPLIT_KEY[1], count: split.gaveUp },
  ]
}

function describeSplit(split: Split): string {
  return `${split.handedToTeam.toLocaleString()} handed to team · ${split.gaveUp.toLocaleString()} gave up`
}
